import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  AvatarGroup,
  Paper,
  Fade,
  Slide,
  Button,
  IconButton,
} from '@mui/material';
import {
  Code,
  Business,
  Group,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';

const SkillsExplorer = () => {
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [showAllTechnical, setShowAllTechnical] = useState(false);
  const [showAllNonTechnical, setShowAllNonTechnical] = useState(false);

  // Sample data - in real app, this would come from API
  const technicalSkills = [
    {
      name: 'Python',
      count: 12,
      employees: ['Arnav Simer', 'Divya Mehta', 'Rishabh Gupta', 'Sarah Johnson', 'Mike Chen', 'Lisa Wang', 'David Kumar', 'Emma Rodriguez', 'Alex Thompson', 'Priya Patel', 'James Wilson', 'Sofia Garcia'],
      color: '#4CAF50'
    },
    {
      name: 'React',
      count: 8,
      employees: ['Arnav Simer', 'Divya Mehta', 'Rishabh Gupta', 'Sarah Johnson', 'Mike Chen', 'Lisa Wang', 'David Kumar', 'Emma Rodriguez'],
      color: '#2196F3'
    },
    {
      name: 'JavaScript',
      count: 15,
      employees: ['Arnav Simer', 'Divya Mehta', 'Rishabh Gupta', 'Sarah Johnson', 'Mike Chen', 'Lisa Wang', 'David Kumar', 'Emma Rodriguez', 'Alex Thompson', 'Priya Patel', 'James Wilson', 'Sofia Garcia', 'Tom Brown', 'Anna Lee', 'Chris Davis'],
      color: '#FF9800'
    },
    {
      name: 'Node.js',
      count: 6,
      employees: ['Arnav Simer', 'Rishabh Gupta', 'Mike Chen', 'David Kumar', 'Alex Thompson', 'James Wilson'],
      color: '#8BC34A'
    },
    {
      name: 'SQL',
      count: 10,
      employees: ['Divya Mehta', 'Sarah Johnson', 'Lisa Wang', 'Emma Rodriguez', 'Priya Patel', 'Sofia Garcia', 'Tom Brown', 'Anna Lee', 'Chris Davis', 'Mark Taylor'],
      color: '#9C27B0'
    },
    {
      name: 'AWS',
      count: 5,
      employees: ['Arnav Simer', 'Rishabh Gupta', 'Alex Thompson', 'James Wilson', 'Mark Taylor'],
      color: '#FF5722'
    }
  ];

  const nonTechnicalSkills = [
    {
      name: 'Project Management',
      count: 8,
      employees: ['Divya Mehta', 'Sarah Johnson', 'Lisa Wang', 'Emma Rodriguez', 'Priya Patel', 'Sofia Garcia', 'Tom Brown', 'Anna Lee'],
      color: '#E91E63'
    },
    {
      name: 'Leadership',
      count: 6,
      employees: ['Arnav Simer', 'Divya Mehta', 'Rishabh Gupta', 'Sarah Johnson', 'Lisa Wang', 'Emma Rodriguez'],
      color: '#3F51B5'
    },
    {
      name: 'Communication',
      count: 14,
      employees: ['Arnav Simer', 'Divya Mehta', 'Rishabh Gupta', 'Sarah Johnson', 'Mike Chen', 'Lisa Wang', 'David Kumar', 'Emma Rodriguez', 'Alex Thompson', 'Priya Patel', 'James Wilson', 'Sofia Garcia', 'Tom Brown', 'Anna Lee'],
      color: '#00BCD4'
    },
    {
      name: 'Problem Solving',
      count: 12,
      employees: ['Arnav Simer', 'Divya Mehta', 'Rishabh Gupta', 'Sarah Johnson', 'Mike Chen', 'Lisa Wang', 'David Kumar', 'Emma Rodriguez', 'Alex Thompson', 'Priya Patel', 'James Wilson', 'Sofia Garcia'],
      color: '#795548'
    },
    {
      name: 'Team Collaboration',
      count: 11,
      employees: ['Arnav Simer', 'Divya Mehta', 'Rishabh Gupta', 'Sarah Johnson', 'Mike Chen', 'Lisa Wang', 'David Kumar', 'Emma Rodriguez', 'Alex Thompson', 'Priya Patel', 'James Wilson'],
      color: '#607D8B'
    },
    {
      name: 'Time Management',
      count: 9,
      employees: ['Divya Mehta', 'Sarah Johnson', 'Lisa Wang', 'Emma Rodriguez', 'Priya Patel', 'Sofia Garcia', 'Tom Brown', 'Anna Lee', 'Chris Davis'],
      color: '#FFC107'
    }
  ];

  const SkillCard = ({ skill, index }) => (
    <Fade in timeout={300 + index * 100}>
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #e0e0e0',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          },
        }}
        onClick={() => setSelectedSkill(skill)}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0D004A' }}>
              {skill.name}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Group sx={{ color: '#666', fontSize: '1.2rem' }} />
            <Typography variant="body2" sx={{ color: '#666', fontWeight: 'medium' }}>
              {skill.count} employees
            </Typography>
          </Box>

          <AvatarGroup max={4} sx={{ justifyContent: 'flex-start' }}>
            {skill.employees.slice(0, 4).map((employee, idx) => (
              <Avatar
                key={idx}
                sx={{
                  bgcolor: skill.color,
                  width: 32,
                  height: 32,
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                }}
              >
                {employee.split(' ').map(n => n[0]).join('')}
              </Avatar>
            ))}
            {skill.employees.length > 4 && (
              <Avatar sx={{ bgcolor: '#f5f5f5', color: '#666', width: 32, height: 32, fontSize: '0.8rem' }}>
                +{skill.employees.length - 4}
              </Avatar>
            )}
          </AvatarGroup>
        </CardContent>
      </Card>
    </Fade>
  );

  return (
    <Box sx={{ width: '100%', p: 4, backgroundColor: 'white' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0D004A', mb: 2}}>
          Skills Explorer
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', maxWidth: '1050px' }}>
          Discover and explore the diverse skills within your organization. Click on any skill to see detailed information about team members who possess that expertise.
        </Typography>
      </Box>

      {/* Technical Skills Section */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Code sx={{ color: '#0D004A', fontSize: '2rem' }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0D004A' }}>
            Technical Skills
          </Typography>
          <Chip
            label={`${technicalSkills.length} skills`}
            sx={{
              backgroundColor: '#0D004A',
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        </Box>
        
        {/* First 4 skills in full width with See More button */}
        <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
          {technicalSkills.slice(0, 4).map((skill, index) => (
            <Grid item xs={12} sm={6} md={3} key={skill.name}>
              <SkillCard skill={skill} index={index} />
            </Grid>
          ))}
          
          {/* See More/Less Button positioned after 4th skill */}
          {technicalSkills.length > 4 && (
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '200px'
              }}>
                <Button
                  variant="outlined"
                  endIcon={showAllTechnical ? <ExpandLess /> : <ExpandMore />}
                  onClick={() => setShowAllTechnical(!showAllTechnical)}
                  sx={{
                    borderColor: '#0D004A',
                    color: '#0D004A',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    px: 3,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#0D004A',
                      color: 'white',
                    },
                  }}
                >
                  {showAllTechnical ? 'See Less' : `See More`}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Additional skills when expanded */}
        {showAllTechnical && technicalSkills.length > 4 && (
          <Grid container spacing={3}>
            {technicalSkills.slice(4).map((skill, index) => (
              <Grid item xs={12} sm={6} md={4} key={skill.name}>
                <SkillCard skill={skill} index={index + 4} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Non-Technical Skills Section */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Business sx={{ color: '#0D004A', fontSize: '2rem' }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0D004A' }}>
            Non-Technical Skills
          </Typography>
          <Chip
            label={`${nonTechnicalSkills.length} skills`}
            sx={{
              backgroundColor: '#0D004A',
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        </Box>
        
        {/* First 4 skills in full width with See More button */}
        <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
          {nonTechnicalSkills.slice(0, 4).map((skill, index) => (
            <Grid item xs={12} sm={6} md={3} key={skill.name}>
              <SkillCard skill={skill} index={index} />
            </Grid>
          ))}
          
          {/* See More/Less Button positioned after 4th skill */}
          {nonTechnicalSkills.length > 4 && (
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '200px'
              }}>
                <Button
                  variant="outlined"
                  endIcon={showAllNonTechnical ? <ExpandLess /> : <ExpandMore />}
                  onClick={() => setShowAllNonTechnical(!showAllNonTechnical)}
                  sx={{
                    borderColor: '#0D004A',
                    color: '#0D004A',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    px: 3,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#0D004A',
                      color: 'white',
                    },
                  }}
                >
                  {showAllNonTechnical ? 'See Less' : `See More`}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Additional skills when expanded */}
        {showAllNonTechnical && nonTechnicalSkills.length > 4 && (
          <Grid container spacing={3}>
            {nonTechnicalSkills.slice(4).map((skill, index) => (
              <Grid item xs={12} sm={6} md={4} key={skill.name}>
                <SkillCard skill={skill} index={index + 4} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Skill Detail Modal */}
      {selectedSkill && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            p: 2,
          }}
          onClick={() => setSelectedSkill(null)}
        >
          <Slide direction="up" in timeout={300}>
            <Paper
              sx={{
                maxWidth: 600,
                width: '100%',
                maxHeight: '80vh',
                overflow: 'auto',
                borderRadius: 3,
                p: 4,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0D004A' }}>
                  {selectedSkill.name} - Team Members
                </Typography>
                <IconButton onClick={() => setSelectedSkill(null)}>
                  ×
                </IconButton>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                  {selectedSkill.count} employees have this skill
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedSkill.employees.map((employee, index) => (
                  <Chip
                    key={index}
                    label={employee}
                    sx={{
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      fontWeight: 'medium',
                    }}
                  />
                ))}
              </Box>
            </Paper>
          </Slide>
        </Box>
      )}
    </Box>
  );
};

export default SkillsExplorer;
